/**
 * 导入向导：选文件 → 预览摘要 → 执行
 * 支持 JSON（完整备份）和 Excel（.xlsx，内容迁移）两种格式。
 * 本地模式 / 云端模式共用同一组件。
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateExport, planMerge, type DayNoteExport } from '@daynote/core';
import { getDataSource } from '../data';
import type { ImportSummary } from '../data/types';

type Step = 'pick' | 'preview' | 'done' | 'error';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export function ImportWizard({ open, onClose }: Props) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('pick');
  const [errorMsg, setErrorMsg] = useState('');
  const [payload, setPayload] = useState<DayNoteExport | null>(null);
  const [previewSummary, setPreviewSummary] = useState<ImportSummary | null>(null);
  const [resultSummary, setResultSummary] = useState<ImportSummary | null>(null);
  const [importing, setImporting] = useState(false);
  const [isExcel, setIsExcel] = useState(false);

  if (!open) return null;

  const reset = () => {
    setStep('pick');
    setErrorMsg('');
    setPayload(null);
    setPreviewSummary(null);
    setResultSummary(null);
    setImporting(false);
    setIsExcel(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setErrorMsg(t('importWizard.errFileTooLarge'));
      setStep('error');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();

    if (ext === 'xlsx') {
      try {
        const { parseExcel } = await import('../lib/excel');
        const parsed = await parseExcel(file);
        if (parsed.data.dayNotes.length === 0) {
          setErrorMsg(t('importWizard.errExcelEmpty'));
          setStep('error');
          return;
        }
        const previewPlan = planMerge(parsed, { groupAbbrs: new Set(), itemKeys: new Set() });
        setPayload(parsed);
        setIsExcel(true);
        setPreviewSummary({
          groupsCreated: 0,
          dayNotesTouched: parsed.data.dayNotes.length,
          itemsImported: previewPlan.itemsToInsert.length,
          itemsSkipped: 0,
        });
        setStep('preview');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : t('importWizard.errExcelParse'));
        setStep('error');
      }
      return;
    }

    // JSON path
    let raw: unknown;
    try {
      const text = await file.text();
      raw = JSON.parse(text);
    } catch {
      setErrorMsg(t('importWizard.errJsonParse'));
      setStep('error');
      return;
    }

    const result = validateExport(raw);
    if (!result.ok) {
      setErrorMsg(t('importWizard.errValidation', { error: result.error }));
      setStep('error');
      return;
    }

    const previewPlan = planMerge(result.data, { groupAbbrs: new Set(), itemKeys: new Set() });
    setPayload(result.data);
    setPreviewSummary({
      groupsCreated: result.data.data.groups.length,
      dayNotesTouched: result.data.data.dayNotes.length,
      itemsImported: previewPlan.itemsToInsert.length,
      itemsSkipped: 0,
    });
    setStep('preview');
  };

  const handleImport = async () => {
    if (!payload) return;
    setImporting(true);
    try {
      const summary = await getDataSource().importData(payload);
      setResultSummary(summary);
      setStep('done');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('importWizard.errImport'));
      setStep('error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--panel-bg)] border border-[var(--grid-line)] rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-xl">{t('importWizard.title')}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-daynote-muted hover:text-daynote-text bg-transparent border-none cursor-pointer text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Step: pick file */}
        {step === 'pick' && (
          <div>
            <p className="text-sm text-daynote-muted mb-4 leading-relaxed">
              {t('importWizard.pickDesc')}
            </p>
            <ul className="text-xs text-daynote-muted mb-4 space-y-1 pl-1">
              <li>
                <span className="text-daynote-accent font-medium">JSON</span>
                {' '}{t('importWizard.formatJsonDesc')}
              </li>
              <li>
                <span className="text-daynote-accent font-medium">Excel (.xlsx)</span>
                {' '}{t('importWizard.formatExcelDesc')}
              </li>
            </ul>
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-[var(--grid-line)] hover:border-daynote-accent rounded-xl p-8 text-center transition-colors">
                <div className="text-3xl mb-2">📂</div>
                <div className="text-sm text-daynote-muted">{t('importWizard.pickCta')}</div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".json,.xlsx,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
          </div>
        )}

        {/* Step: preview */}
        {step === 'preview' && payload && previewSummary && (
          <div>
            <p className="text-sm text-daynote-muted mb-4">
              {isExcel ? (
                <span>
                  {t('importWizard.previewSourceExcel')}<span className="text-daynote-text">{t('importWizard.previewSourceExcelVal')}</span>
                </span>
              ) : (
                <>
                  {t('importWizard.previewSourceLabel')}<span className="text-daynote-text">{payload.source === 'cloud' ? t('importWizard.previewSourceCloud') : t('importWizard.previewSourceLocal')}</span>
                  {t('importWizard.previewExportedAt')}<span className="text-daynote-text">{payload.exportedAt.slice(0, 10)}</span>
                </>
              )}
            </p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: t('importWizard.previewGroups'), value: previewSummary.groupsCreated },
                { label: t('importWizard.previewDays'),   value: previewSummary.dayNotesTouched },
                { label: t('importWizard.previewItems'),  value: previewSummary.itemsImported },
              ].map((s) => (
                <div
                  key={s.label}
                  className="text-center py-3 rounded-lg bg-white/[0.04] border border-[var(--grid-line)]"
                >
                  <div className="font-display text-2xl font-medium">{s.value}</div>
                  <div className="text-[11px] text-daynote-text/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            {isExcel && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-daynote-warm/10 border border-daynote-warm/30 text-xs text-daynote-muted leading-relaxed">
                {t('importWizard.excelWarning')}
              </div>
            )}
            <p className="text-xs text-daynote-muted mb-5 leading-relaxed">
              {t('importWizard.dedupeNote')}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={reset}
                className="flex-1 py-2.5 text-sm border border-[var(--grid-line)] text-daynote-muted rounded-lg bg-transparent cursor-pointer hover:border-daynote-accent hover:text-daynote-text transition-colors"
              >
                {t('importWizard.reselect')}
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing}
                className="flex-1 py-2.5 text-sm bg-daynote-accent text-[#2a2520] font-semibold rounded-lg border-none cursor-pointer disabled:opacity-60"
              >
                {importing ? t('importWizard.importing') : t('importWizard.confirm')}
              </button>
            </div>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && resultSummary && (
          <div className="text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-lg font-medium mb-4">{t('importWizard.successTitle')}</p>
            <div className="grid grid-cols-2 gap-3 mb-5 text-left">
              {[
                { label: t('importWizard.doneGroups'),  value: resultSummary.groupsCreated },
                { label: t('importWizard.doneDays'),    value: resultSummary.dayNotesTouched },
                { label: t('importWizard.doneItems'),   value: resultSummary.itemsImported },
                { label: t('importWizard.doneSkipped'), value: resultSummary.itemsSkipped },
              ].map((s) => (
                <div
                  key={s.label}
                  className="py-2.5 px-3 rounded-lg bg-white/[0.04] border border-[var(--grid-line)]"
                >
                  <div className="font-display text-xl font-medium">{s.value}</div>
                  <div className="text-[11px] text-daynote-text/50 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-2.5 text-sm bg-daynote-accent text-[#2a2520] font-semibold rounded-lg border-none cursor-pointer"
            >
              {t('importWizard.done')}
            </button>
          </div>
        )}

        {/* Step: error */}
        {step === 'error' && (
          <div>
            <div className="text-center text-4xl mb-3">⚠️</div>
            <p className="text-sm text-daynote-muted text-center mb-4 leading-relaxed">{errorMsg}</p>
            <button
              type="button"
              onClick={reset}
              className="w-full py-2.5 text-sm border border-[var(--grid-line)] text-daynote-muted rounded-lg bg-transparent cursor-pointer hover:border-daynote-accent hover:text-daynote-text transition-colors"
            >
              {t('importWizard.reselectFile')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
